export const ZEBRA_UNKNOWN_ERROR_CODE = 999;
export const ZEBRA_UNKNOWN_ERROR_MESSAGE = "Unknown Error";

export type ZebraCatalogEntry = {
  code: number;
  message: string;
  /** Case-insensitive substrings matched against API/wrapper text (more specific first). */
  match: string[];
};

/**
 * Central catalog for Zebra Browser Print / hardware status failures.
 * Display format: `<message> [<code>]`
 */
export const ZEBRA_ERROR_CATALOG: ZebraCatalogEntry[] = [
  // Printer discovery / default device
  {
    code: 123,
    message: "Printer not found",
    match: [
      "printer not found",
      "there's no default printer",
      "there is no default printer",
      "no default printer",
    ],
  },
  {
    code: 124,
    message: "No printers available",
    match: ["no printers available"],
  },

  // Write / connection failures (specific before generic)
  {
    code: 201,
    message: "Error writing to port, connection closed",
    match: ["error writing to port", "connection closed"],
  },
  {
    code: 202,
    message: "Unable to establish connection",
    match: ["unable to establish connection"],
  },
  {
    code: 203,
    message: "Failed to write to device",
    match: ["failed to write to device", "could not print to printer"],
  },

  // Browser Print service / network
  {
    code: 301,
    message: "Zebra Browser Print service unavailable",
    match: [
      "failed to fetch",
      "networkerror",
      "network request failed",
      "load failed",
      "err_connection_refused",
      "econnrefused",
    ],
  },

  // Hardware flags from checkPrinterStatus
  { code: 401, message: "Paper out", match: ["paper out"] },
  { code: 402, message: "Ribbon Out", match: ["ribbon out"] },
  { code: 403, message: "Media Door Open", match: ["media door open"] },
  { code: 404, message: "Cutter Fault", match: ["cutter fault"] },
  { code: 405, message: "Printhead Overheating", match: ["printhead overheating"] },
  { code: 406, message: "Motor Overheating", match: ["motor overheating"] },
  { code: 407, message: "Printhead Fault", match: ["printhead fault"] },
  { code: 408, message: "Incorrect Printhead", match: ["incorrect printhead"] },
  {
    code: 409,
    message: "Printer Paused",
    match: ["printer paused", "pause active"],
  },

  // Wrapper fallback when status bits are empty
  {
    code: ZEBRA_UNKNOWN_ERROR_CODE,
    message: ZEBRA_UNKNOWN_ERROR_MESSAGE,
    match: ["unknown error", "error: unknown error"],
  },
];

export type ZebraErrorContext = {
  httpStatus?: number;
  body?: string;
};

const toText = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || String(value);
  if (typeof value === "object") {
    const maybe = value as { message?: unknown; body?: unknown };
    if (typeof maybe.message === "string" && maybe.message.trim()) {
      return maybe.message;
    }
    if (typeof maybe.body === "string" && maybe.body.trim()) {
      return maybe.body;
    }
  }
  try {
    return String(value);
  } catch {
    return "";
  }
};

export const findZebraCatalogEntry = (
  rawText: string
): ZebraCatalogEntry | undefined => {
  const normalized = rawText.toLowerCase().trim();
  if (!normalized) return undefined;

  return ZEBRA_ERROR_CATALOG.find((entry) =>
    entry.match.some((fragment) => normalized.includes(fragment.toLowerCase()))
  );
};

/**
 * Resolve a Zebra failure into legacy toast text: `<message> [<code>]`.
 * Prefers catalog message when mapped; otherwise uses API body/text with [999].
 */
export const resolveZebraError = (
  raw: unknown,
  context?: ZebraErrorContext
): string => {
  const apiText =
    (typeof context?.body === "string" && context.body.trim()) ||
    toText(raw).trim();

  const entry = findZebraCatalogEntry(apiText);
  if (entry) {
    return `${entry.message} [${entry.code}]`;
  }

  const message = apiText || ZEBRA_UNKNOWN_ERROR_MESSAGE;
  return `${message} [${ZEBRA_UNKNOWN_ERROR_CODE}]`;
};

export const formatZebraErrorForLog = (
  resolved: string,
  context?: ZebraErrorContext
): string => {
  const parts = [resolved];
  if (context?.httpStatus != null) {
    parts.push(`HTTP ${context.httpStatus}`);
  }
  if (context?.body?.trim()) {
    parts.push(`body: ${context.body.trim()}`);
  }
  return parts.join(" | ");
};
